import { store } from "store";
//#region redux
import { useDispatch } from "react-redux";
import { selfInvoiceSent } from "store/actions/selfInvoiceActions";
//#endregion
//#region types
import { AuthEnums, InvoiceEnums } from "models/enums";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { CommonConstants, FormFieldConstants, InvoiceConstants, ServiceConstants } from "utils/constants";
import { InvoiceFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { SelectionItem } from "models/utilTypes/selectionTypes";
//#endregion
//#region components
import useAPIClientHelper from "./useAPIClientHelper";
//#endregion
import {
  ApiClientInstance,
  DateHelper,
  EnumHelper,
  FileHelper,
  ForwardedCostHelper,
  InvoiceWarningHelper,
  InvoiceHelper,
  LocalizationHelperInstance,
  UtilsHelper,
} from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const invoiceWarningsApiUrl = "api/Invoice/Warnings";

function useInvoiceService() {
  const dispatch = useDispatch();
  const { apiClientInstance } = useAPIClientHelper();

  const get = (id: number, key?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetById(id)
      .then((res) => ({ ...res.data, key: key }));
  };

  const deleteInvoice = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.Delete(id);
  };

  const reminderPayment = (id: number, form: WintSuperkollAPIModel.InvoicePaymentReminder) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.SendPaymentReminder(id, form);
  };

  const writeOffReminderFee = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.WriteOffReminderFee(id);
  };
  const getByFilter = (filter: WintSuperkollAPIModel.InvoiceFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).InvoiceClient.GetList(filter);
  };

  const postClaim = (id: number, sendToDebtCollectionForm: WintSuperkollAPIModel.SendToDebtCollectionForm) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.PostClaim(id, sendToDebtCollectionForm);
  };

  const getCustomerLink = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.GetCustomerLink(id);
  };

  const editDueDate = (id: number, payment: WintSuperkollAPIModel.InvoiceDue) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.EditDueDate(id, payment);
  };

  const reportAsPaid = (id: number, payment: WintSuperkollAPIModel.ReportPaymentRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.ReportAsPaid(id, payment);
  };

  const createInvoice = (invoicePost: WintSuperkollAPIModel.InvoicePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .InvoiceClient.Post(invoicePost)
      .then((res) => res.data);
  };

  const updateInvoice = (invoiceId: number, invoice: WintSuperkollAPIModel.InvoicePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.Update(invoiceId, invoice);
  };

  const editPeriodisations = (invoiceId: number, body: WintSuperkollAPIModel.InvoicePeriodisationsPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.EditPeriodisations(invoiceId, body);
  };

  const createOrUpdateInvoice = async (
    id?: number,
    invoice?: WintSuperkollAPIModel.InvoicePut,
    preventStrictValidation?: boolean
  ): Promise<number | undefined> => {
    let invoiceId = id;

    if (invoice) {
      invoice = { ...invoice, useStrictValidation: !preventStrictValidation };

      if (id) {
        await updateInvoice(id, invoice);
      } else {
        invoiceId = await createInvoice(invoice);

        // temporary code which checks if attachments have been saved and if not saves
        // them in a separate call. this is to avoid error because of a breaking change
        // in SuperkollAPI where the server starts to save attachments which it did do
        // before. once this SuperkollAPI change has been released this code can be removed.
        try {
          if (invoice.attachments) {
            const savedAttachments = await apiClientInstance(AuthEnums.AuthType.Company, {
              showErrors: false,
            }).InvoiceClient.GetAttachments(invoiceId);
            if (!savedAttachments.data.length) {
              await postAttachments(invoiceId, invoice.attachments);
            }
          }
        } catch (error) {
          return invoiceId;
        }
      }
    }

    const relatedSelfInvoiceId = invoice?.selfInvoiceId;
    if (relatedSelfInvoiceId && invoice?.invoiceSource !== WintSuperkollAPIModel.InvoiceSource.SelfInvoice) {
      await ApiClientInstance(AuthEnums.AuthType.Company, {
        showErrors: true,
      }).SelfInvoiceClient.MarkSelfInvoiceAsCreatedAsync(relatedSelfInvoiceId);

      dispatch(selfInvoiceSent(relatedSelfInvoiceId));
    }

    return invoiceId;
  };

  const sendAsEmail = (invoiceId: number, mailOptions: WintSuperkollAPIModel.InvoiceMailOptions) => {
    const model: WintSuperkollAPIModel.InvoiceSendProperties = {
      invoiceSendMethod: WintSuperkollAPIModel.InvoiceSendMethod.Email,
      mailOptions: mailOptions,
    };
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.Send(invoiceId, model);
  };

  const emailInvoice = (invoiceId: number, mailOptions: WintSuperkollAPIModel.InvoiceMailOptions) => {
    const model: WintSuperkollAPIModel.InvoiceSendProperties = {
      invoiceSendMethod: WintSuperkollAPIModel.InvoiceSendMethod.Email,
      mailOptions: mailOptions,
    };
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.MailInvoice(invoiceId, model);
  };

  const sendAsPrint = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .InvoiceClient.Send(invoiceId, {
        invoiceSendMethod: WintSuperkollAPIModel.InvoiceSendMethod.Print,
      })
      .then((res) => res.data);
  };

  const printInvoice = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .InvoiceClient.SendEInvoice(invoiceId)
      .then((res) => res.data);
  };

  const markAsSent = async (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.Send(id, {
      invoiceSendMethod: WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent,
    });
  };

  const downloadPdf = async (id: number, version?: number) => {
    const pdfResult = await apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).InvoiceClient.DownloadInvoicePdf(id, version);
    FileHelper.download(pdfResult);
  };

  const getInvoicePdf = async (id: number, version?: number | undefined) => {
    return await apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetInvoicePdf(id, version)
      .then((res) => res.data);
  };

  const createReferencePerson = (referencePerson: WintSuperkollAPIModel.ReferencePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReferenceClient.Post(referencePerson);
  };

  const updateReferencePerson = (referenceId: number, referencePerson: WintSuperkollAPIModel.ReferencePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReferenceClient.Put(referenceId, referencePerson);
  };

  const postAttachments = (invoiceId: number, attachments: WintSuperkollAPIModel.InvoiceAttachmentPost[]) => {
    const promises = attachments.map((attachment) =>
      apiClientInstance(AuthEnums.AuthType.Company, {
        showErrors: true,
      }).InvoiceClient.PostAttachment(invoiceId, attachment)
    );

    return Promise.all(promises);
  };

  const getAttachment = (invoiceId: number, attachmentId: string | number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetAttachment(invoiceId, attachmentId.toString())
      .then((res) => res.data.data ?? "");
  };

  const getInvoiceMailForm = (request: WintSuperkollAPIModel.InvoiceDefaultMailOptionsRequest, id?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetDefaultMailOptions(request, id)
      .then((data) => data.data);
  };

  const getUIViewState = (invoice: WintSuperkollAPIModel.Invoice) => {
    //TODO: Check other required fields needed to fullfil
    if (invoice.postingDate === undefined) {
      return InvoiceEnums.InvoiceViewState.Draft;
    }
    if (invoice.status === WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent) {
      return InvoiceEnums.InvoiceViewState.Overdue;
    }

    return InvoiceEnums.InvoiceViewState.DraftNotSent;
  };

  const getStateText = (state: WintSuperkollAPIModel.InvoiceStatus) => {
    return LocalizationHelperInstance.getFormattedMessage(`Invoice.State_${state}`);
  };

  const getAccountingStateText = (state: WintSuperkollAPIModel.InvoiceStatus) => {
    switch (state) {
      case WintSuperkollAPIModel.InvoiceStatus.NotSent:
        return LocalizationHelperInstance.getFormattedMessage(`Invoice.Draft`);
      case WintSuperkollAPIModel.InvoiceStatus.Cancelled:
        return LocalizationHelperInstance.getFormattedMessage(`Invoice.Discarded`);
      default:
        return LocalizationHelperInstance.getFormattedMessage(`Invoice.InBooks`);
    }
  };

  const getTransactions = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetTransactions(id)
      .then((res) => res.data);
  };

  const getSimilarInvoices = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.FindSimilar(id, 7)
      .then((res) => res.data);
  };

  const getStateLogs = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetStateLogs(id)
      .then((res) => res.data);
  };

  const customerReferencePersons = (customerId: number, pageNumber: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).ReferenceClient.GetByFilter({
      ...ServiceConstants.DefaultFilter,
      customerId: customerId,
      page: pageNumber,
    });
  };

  const getReferencePersonSuggestions = (customerId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReferenceClient.GetTopReferencePersons(customerId)
      .then((res) => res.data);
  };

  const searchReferencePersons = (
    customerId: number,
    searchText: string
  ): Promise<WintSuperkollAPIModel.ReferenceSearchResponse[]> => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReferenceClient.GetSearchReferencePersons(customerId, searchText)
      .then((res) => res.data ?? []);
  };

  const searchReferencePersonsByMail = (
    customerId: number,
    searchText: string
  ): Promise<WintSuperkollAPIModel.ReferenceSearchResponse[]> => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReferenceClient.GetSearchReferencePersonsByMail(customerId, searchText)
      .then((res) => res.data ?? []);
  };

  const getUnits = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .UnitClient.GetByFilter({
        ...ServiceConstants.DefaultFilter,
        numPerPage: 1000,
      } as WintSuperkollAPIModel.UnitFilter)
      .then((res) => res.data.items ?? []);
  };

  const generateInvoicePreview = (invoice: WintSuperkollAPIModel.InvoicePreviewRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "text",
    }).InvoiceClient.PreviewInvoicePdf(invoice);
  };

  const getInvoicePDFPreview = async (id: number, version?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "text",
    }).InvoiceClient.PreviewInlineInvoicePdf(id, version);
  };

  const getInvoiceReminderPDFPreview = async (
    id: number,
    invoicePaymentReminder: WintSuperkollAPIModel.InvoicePaymentReminder
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "text",
    }).InvoiceClient.InvoiceReminderPreviewPdf(id, invoicePaymentReminder);
  };

  const getSellers = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetTopSellers()
      .then((res) => res.data);
  };

  const getPaymentOptions = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoicePaymentOptionClient.Get({} as WintUtilsWebApi.Filter)
      .then((res) => res.data.items);
  };

  const getArticleSuggestions = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ArticleClient.TopArticle()
      .then((res) => res.data);
  };

  const searchArticles = (searchText: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ArticleClient.SearchArticle(searchText)
      .then((res) => res.data);
  };

  const getCompatibleDimensionTypes = (isQuotation?: boolean) => {
    return isQuotation
      ? [
          WintSuperkollAPIModel.DimensionCompatibilityType.Project,
          WintSuperkollAPIModel.DimensionCompatibilityType.CostCenter,
        ]
      : EnumHelper.getNumericItemValues(WintSuperkollAPIModel.DimensionCompatibilityType);
  };

  const updateDimensions = (invoiceId: number, dimensions: string[], useStrictValidations: boolean) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .InvoiceClient.UpdateDimensions(invoiceId, {
        useStrictValidation: useStrictValidations,
        dimensionList: dimensions,
      })
      .then((res) => res.data);
  };

  const getEuVatCategoryTypes = (): AutocompleteListItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.EuVatProductType).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`Invoice.EUVATCategory_${key}`),
      };
    });
  };

  const getEuVatCategoryText = (key: WintSuperkollAPIModel.EuVatProductType) =>
    LocalizationHelperInstance.getFormattedMessage(`Invoice.EUVATCategory_${key}`);

  const calculateTax = (standardPrice: number, taxPercentage: number, quantity: number = 1) => {
    return ((standardPrice * taxPercentage) / 100) * quantity;
  };

  const calculateAmount = (standardPrice: number, quantity: number = 1) => {
    return standardPrice * quantity;
  };

  const calculateTotalFromProducts = (products: InvoiceFormTypes.Product[]) => {
    return products.map((p) => calculateAmount(p.price ?? 0, p.quantity)).reduce((a, b) => +a + +b, 0);
  };

  const getRowDimensionDataAdvancedConfig = (totalAmount: number, value?: InvoiceFormTypes.Product[]) => {
    if (value?.length) {
      return (
        value[0].dimensions?.map(
          (dimension): WintSuperkollAPIModel.DimensionData => ({
            dimensionId: dimension,
            amount: totalAmount,
          })
        ) ?? []
      );
    }

    return [];
  };

  const getRowDimensionData = (value?: InvoiceFormTypes.Product[] | WintSuperkollAPIModel.InvoiceRow[]) => {
    const selectedDimensions =
      (value as any[])?.flatMap(
        (product: InvoiceFormTypes.Product | WintSuperkollAPIModel.InvoiceRow) =>
          product.dimensions?.map(
            (dimension): WintSuperkollAPIModel.DimensionData => ({
              dimensionId: dimension,
              amount:
                "price" in product
                  ? calculateAmount(product.price ?? 0, product.quantity)
                  : "unitPrice" in product
                    ? calculateAmount(product.overriddenUnitPrice ?? product.unitPrice ?? 0, product.quantity)
                    : 0,
            })
          ) ?? []
      ) ?? [];

    const result: WintSuperkollAPIModel.DimensionData[] = [];

    selectedDimensions.reduce<Record<string, WintSuperkollAPIModel.DimensionData>>(
      (res: Record<string, WintSuperkollAPIModel.DimensionData>, value: WintSuperkollAPIModel.DimensionData) => {
        if (value.dimensionId) {
          if (!res[value.dimensionId]) {
            res[value.dimensionId] = {
              dimensionId: value.dimensionId,
              amount: value.amount,
            };
            result.push(res[value.dimensionId]);
          } else if (res[value.dimensionId]) {
            res[value.dimensionId].amount += value.amount;
          }
        }
        return res;
      },
      {}
    );

    return result;
  };

  const getInvoiceDimensionData = (amount: number, value?: string[]) => {
    return (
      value &&
      value.map(
        (dimension): WintSuperkollAPIModel.DimensionData => ({
          amount: amount,
          dimensionId: dimension,
        })
      )
    );
  };

  const getInvoiceDimensions = (values: any): string[] => {
    return FormFieldConstants.Invoice.Details.Dimensions in values
      ? values.dimensions.map((dimension: any): WintSuperkollAPIModel.DimensionData => dimension.dimensionId)
      : [];
  };

  const getUpdatedInvoice = (
    invoice: WintSuperkollAPIModel.Invoice,
    values: any
  ): WintSuperkollAPIModel.InvoicePost => {
    return {
      useStrictValidation: true,
      ownerId: invoice.ownerId,
      customerId: invoice.customerId,
      customerReference: invoice.customerReference,
      contactPerson: invoice.contactPerson,
      notes: invoice.notes,
      dimensions: FormFieldConstants.Invoice.Details.Dimensions in values ? values.dimension : [],
      deliveryDate: invoice.deliveryDate,
      postingDate: invoice.postingDate,
      dueDate: invoice.dueDate,
      showReferencePersonInAddress: invoice.showReferencePersonInAddress,
      showShippingAddress: invoice.showShippingAddress,
      showCurrencyCode: invoice.showCurrencyCode,
      language: invoice.language,
      currency: invoice.currency,
      creditedInvoiceId: invoice.creditedInvoiceId,
      reminderCost: invoice.reminderCost,
      reminderNotes: invoice.reminderNotes,
      paymentOptionId: invoice.paymentOptionId,
      rows: invoice.rows,
      externalInvoiceReferences: invoice.externalInvoiceReferences,
      template: invoice.template,
      penaltyInterest: invoice.penaltyInterest,
      invoiceHouseWorkType: invoice.invoiceHouseWorkType,
      invoiceRotPercentageType: invoice.invoiceRotPercentageType,
      invoiceRotPropertyType: invoice.invoiceRotPropertyType,
      invoiceSource: invoice.invoiceSource,
      attachForwardedCostDocuments: false,
    };
  };

  const getCreditInvoice = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .InvoiceClient.GetCreditInvoice(invoiceId)
      .then((res) => res.data);
  };

  const getRecurringFromInvoice = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetReccuringFromInvoice(invoiceId)
      .then((res) => res.data);
  };

  const getCopiedInvoice = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetCopiedInvoice(invoiceId)
      .then((res) => res.data);
  };

  const getInvoiceWarnings = (
    invoicePost: WintSuperkollAPIModel.InvoicePost,
    invoice: { id?: number; status?: WintSuperkollAPIModel.InvoiceStatus } = {}
  ): Promise<WintSuperkollAPIModel.Warning[]> => {
    const { body, params } = InvoiceWarningHelper.getInvoiceWarningsRequest(invoicePost, invoice);
    const client = apiClientInstance(AuthEnums.AuthType.Company);
    // The generated GetWarnings has no invoiceId until wint-api-client is regenerated from a Core build that has it.
    return (params ? client.post(invoiceWarningsApiUrl, body, params) : client.InvoiceClient.GetWarnings(body)).then(
      (res) => res.data
    );
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: InvoiceFormTypes.Filter) => {
    return getByFilter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const reportToAPIFilter = (
    formFilter?: InvoiceFormTypes.Filter
  ): Omit<WintSuperkollAPIModel.InvoiceFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      ownerIds: formFilter?.owners?.map((o) => o.id as number),
      customerIds: formFilter?.customers?.map((o) => o.id as number),
      minSerialNumber: formFilter?.serialNumber?.lowest,
      maxSerialNumber: formFilter?.serialNumber?.highest,
      invoiceDateFrom: formFilter?.invoiceDate?.start,
      invoiceDateTo: formFilter?.invoiceDate?.end,
      dueDateFrom: formFilter?.dueDate?.start,
      dueDateTo: formFilter?.dueDate?.end,
      minTotalAmount: formFilter?.invoicedAmount?.lowest,
      maxTotalAmount: formFilter?.invoicedAmount?.highest,
      creditStatuses: formFilter?.creditStatus?.map((i) => i.id as WintSuperkollAPIModel.InvoiceCreditStatus),
      showCreditSameMonth: !formFilter?.ledgerDate ? formFilter?.showCreditSameMonth : false,
      snapshotDate: formFilter?.ledgerDate,
      paymentStatuses: formFilter?.paymentStatuses
        ?.map((p) => ({
          states: toApiViewPaymentStates(p.id as InvoiceEnums.InvoiceViewPaymentState),
        }))
        .flatMap((s) => s.states),
      minUnpaidAmount: formFilter?.unpaidAmount?.lowest,
      maxUnpaidAmount: formFilter?.unpaidAmount?.highest,
      houseWorkInvoice: formFilter?.rotAndRut,
      statuses: formFilter?.invoiceStatuses?.map((s) => s.id as WintSuperkollAPIModel.InvoiceStatus),
      articleIds: formFilter?.articles?.map((o) => o.id as number),
      recurringInvoiceId: formFilter?.recurringInvoiceId,
      showAgentInvoices: formFilter?.agentInvoicesId,
      dimensions: formFilter?.dimensions,
      sources: formFilter?.sources?.map((i) => i.id as WintSuperkollAPIModel.InvoiceSource),
    };
  };

  const getPaymentStatuses = (): AutocompleteListItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoicePaymentState).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`Invoice.PaymentStatus_${key}`),
      };
    });
  };

  const getPaymentStateText = (state: WintSuperkollAPIModel.InvoicePaymentState) => {
    return LocalizationHelperInstance.getFormattedMessage(`Invoice.PaymentStatus_${state}`);
  };

  const getCreditStatuses = (): SelectionItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceCreditStatus).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`Invoice.CreditViewStatus_${key}`),
      };
    });
  };

  const getPaymentViewStatuses = (): AutocompleteListItem[] => {
    return EnumHelper.getNumericItemValues(InvoiceEnums.InvoiceViewPaymentState).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`Invoice.PaymentViewStatus_${key}`),
      };
    });
  };

  const getInvoiceStatuses = (): AutocompleteListItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceStatus)
      .filter((key) => Number(key) !== WintSuperkollAPIModel.InvoiceStatus.Expires)
      .map((key) => {
        return {
          id: Number(key),
          value: LocalizationHelperInstance.getFormattedMessage(`Invoice.State_${key}`),
        };
      });
  };

  const toViewState = (apiState: WintSuperkollAPIModel.InvoiceStatus) => {
    switch (apiState) {
      case WintSuperkollAPIModel.InvoiceStatus.NotSent:
        return InvoiceEnums.InvoiceViewChartState.Draft;
      case WintSuperkollAPIModel.InvoiceStatus.Unpaid:
        return InvoiceEnums.InvoiceViewChartState.Unpaid;
      case WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent:
        return InvoiceEnums.InvoiceViewChartState.OverDue;
      case WintSuperkollAPIModel.InvoiceStatus.OverdueReminderSent:
        return InvoiceEnums.InvoiceViewChartState.ReminderSent;
      case WintSuperkollAPIModel.InvoiceStatus.Collection:
        return InvoiceEnums.InvoiceViewChartState.Collection;
      case WintSuperkollAPIModel.InvoiceStatus.Paid:
        return InvoiceEnums.InvoiceViewChartState.Paid;
    }
  };

  const toApiState = (viewState: InvoiceEnums.InvoiceViewChartState): WintSuperkollAPIModel.InvoiceStatus => {
    switch (viewState) {
      case InvoiceEnums.InvoiceViewChartState.Draft:
        return WintSuperkollAPIModel.InvoiceStatus.NotSent;
      case InvoiceEnums.InvoiceViewChartState.Unpaid:
        return WintSuperkollAPIModel.InvoiceStatus.Unpaid;
      case InvoiceEnums.InvoiceViewChartState.OverDue:
        return WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent;
      case InvoiceEnums.InvoiceViewChartState.ReminderSent:
        return WintSuperkollAPIModel.InvoiceStatus.OverdueReminderSent;
      case InvoiceEnums.InvoiceViewChartState.Collection:
        return WintSuperkollAPIModel.InvoiceStatus.Collection;
      case InvoiceEnums.InvoiceViewChartState.Paid:
        return WintSuperkollAPIModel.InvoiceStatus.Paid;
    }
  };

  const toViewPaymentState = (apiState: WintSuperkollAPIModel.InvoicePaymentState) => {
    switch (apiState) {
      case WintSuperkollAPIModel.InvoicePaymentState.Paid:
        return InvoiceEnums.InvoiceViewPaymentState.Paid;
      case WintSuperkollAPIModel.InvoicePaymentState.PartiallyPaid:
      case WintSuperkollAPIModel.InvoicePaymentState.PartiallyPaidOverdue:
        return InvoiceEnums.InvoiceViewPaymentState.PartiallyPaid;
      case WintSuperkollAPIModel.InvoicePaymentState.Unpaid:
      case WintSuperkollAPIModel.InvoicePaymentState.Overdue:
      case WintSuperkollAPIModel.InvoicePaymentState.OverdueSoon:
        return InvoiceEnums.InvoiceViewPaymentState.Unpaid;
    }
  };

  const toApiViewPaymentStates = (viewState: InvoiceEnums.InvoiceViewPaymentState) => {
    switch (viewState) {
      case InvoiceEnums.InvoiceViewPaymentState.Paid:
        return [WintSuperkollAPIModel.InvoicePaymentState.Paid];
      case InvoiceEnums.InvoiceViewPaymentState.PartiallyPaid:
        return [
          WintSuperkollAPIModel.InvoicePaymentState.PartiallyPaid,
          WintSuperkollAPIModel.InvoicePaymentState.PartiallyPaidOverdue,
        ];
      case InvoiceEnums.InvoiceViewPaymentState.Unpaid:
        return [
          WintSuperkollAPIModel.InvoicePaymentState.Unpaid,
          WintSuperkollAPIModel.InvoicePaymentState.Overdue,
          WintSuperkollAPIModel.InvoicePaymentState.OverdueSoon,
        ];
    }
  };

  const getTodoState = (state: WintSuperkollAPIModel.InvoiceStatus) => {
    if (state === WintSuperkollAPIModel.InvoiceStatus.NotSent) {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Draft);
    } else {
      return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Overdue);
    }
  };

  const getTodoStateLabelClass = (state: WintSuperkollAPIModel.InvoiceStatus) => {
    if (state === WintSuperkollAPIModel.InvoiceStatus.NotSent) {
      return cssExports.yellowTag;
    } else {
      return cssExports.redTag;
    }
  };

  const getProducts = (invoice: WintSuperkollAPIModel.Invoice) => {
    return invoice.dimensions
      ? invoice.rows?.map((row) => ({
          ...row,
          dimensions: invoice.dimensions?.concat(row.dimensions?.filter((d) => !invoice.dimensions?.includes(d)) ?? []),
        }))
      : invoice.rows;
  };

  const convertFormToAPIRequest = (
    form: InvoiceFormTypes.Create,
    noVatCompany: boolean,
    hideDeliveryDate: boolean,
    invoice?: WintSuperkollAPIModel.Invoice
  ): WintSuperkollAPIModel.InvoicePut => {
    const costForwardingFields = ForwardedCostHelper.getCostForwardingRequestFields(form);
    const houseWorkOtherCostsVatPercentage = form.products && form.products.length > 0 ? form.products[0].tax : 0;
    const houseWorkOtherCostExclVAT = form.rutRot?.houseWorkOtherCosts ?? 0;
    const houseWorkOtherCostInclVAT =
      +houseWorkOtherCostExclVAT + Math.round(+houseWorkOtherCostExclVAT * +houseWorkOtherCostsVatPercentage) / 100;
    const serialNumberTemplateValueDisplayText = LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.Invoice.InvoiceNumberHashTag
    );

    return {
      ownerId: form.ownerId,
      customerId: (form.customer?.id as number) ?? 0,
      customerReference: form.customer?.id
        ? {
            referenceId: form.referencePerson?.id as number,
            referenceName: form.referencePerson?.value,
          }
        : undefined,
      rows: form.products?.map((product, index) => {
        const materialCostExclVat =
          form.rutRot?.rutRotArticles?.find((rr) => rr.id === product.uniqueId)?.materialCost ?? 0;
        const materialCostInclVat = +materialCostExclVat + Math.round(+materialCostExclVat * +product.tax) / 100;

        const periodisation = form.isPeriodisationAdvancedConfiguration
          ? product.periodisation
          : form.products![0].periodisation;

        const isMonthMode = (periodisation?.mode ?? "month") === "month";
        const hasRange = periodisation?.range?.start !== undefined && periodisation?.range?.end !== undefined;
        const fromRecurringWithEnabled = invoice?.fromRecurring && !!periodisation?.isEnabled && isMonthMode;

        return {
          id: product.id ?? 0,
          articleId: Number(product.article?.id ?? 0),
          orderIndex: index,
          quantity: Number(product.quantity),
          unit: product.unit ? { id: product.unit } : { id: 0 },
          unitPrice: Number(product.price ?? 0),
          vat: Number(product.tax),
          overriddenVat: Number(noVatCompany ? 0 : product.tax),
          description: product.article?.value ?? "",
          overriddenDescription: product.text,
          overriddenUnitPrice: Number(product.price ?? 0),
          euVatProductType: product.eUVatCategoryId,
          periodisation: invoice?.fromRecurring ? !!periodisation?.isEnabled : hasRange,
          periodisationStartMonth:
            isMonthMode && periodisation?.range?.start
              ? DateHelper.getMonth(periodisation.range.start)
              : fromRecurringWithEnabled
                ? DateHelper.getMonth(form.otherSettings.invoiceDate)
                : undefined,
          periodisationStartYear:
            isMonthMode && periodisation?.range?.start
              ? DateHelper.getYear(periodisation.range.start)
              : fromRecurringWithEnabled
                ? DateHelper.getYear(form.otherSettings.invoiceDate)
                : undefined,
          periodisationEndMonth:
            isMonthMode && periodisation?.range?.end
              ? DateHelper.getMonth(periodisation.range.end)
              : fromRecurringWithEnabled
                ? DateHelper.getMonth(form.otherSettings.invoiceDate)
                : undefined,
          periodisationEndYear:
            isMonthMode && periodisation?.range?.end
              ? DateHelper.getYear(periodisation.range.end)
              : fromRecurringWithEnabled
                ? DateHelper.getYear(form.otherSettings.invoiceDate)
                : undefined,
          periodisationStartDate: !isMonthMode && periodisation?.range?.start ? periodisation.range.start : undefined,
          periodisationEndDate: !isMonthMode && periodisation?.range?.end ? periodisation.range.end : undefined,
          dimensions: form.isDimensionsAdvancedConfiguration ? product.dimensions : [],
          rut:
            form.rutRot?.rutRotArticles?.some((rr) => rr.id === product.uniqueId) && form && form.rutRot?.houseWorkType
              ? form.rutRot?.houseWorkType === WintSuperkollAPIModel.InvoiceHouseWorkType.RUT
              : false,
          rot:
            form.rutRot?.rutRotArticles?.some((rr) => rr.id === product.uniqueId) && form && form.rutRot?.houseWorkType
              ? form.rutRot?.houseWorkType === WintSuperkollAPIModel.InvoiceHouseWorkType.ROT
              : false,
          materialCost: materialCostInclVat,
          houseWorkHours: form.rutRot?.rutRotArticles?.find((rr) => rr.id === product.uniqueId)?.noOfHours,
          houseWorkTypeId: form.rutRot?.rutRotArticles?.find((rr) => rr.id === product.uniqueId)?.typeOfWorkId,
        };
      }),
      contactPerson: form.otherSettings.sellerName,
      notes: form.otherSettings.notes,
      dimensions: form.isDimensionsAdvancedConfiguration
        ? []
        : form.products
          ? (form.products[0]?.dimensions ?? [])
          : [],
      deliveryDate: hideDeliveryDate ? form.otherSettings.invoiceDate! : form.otherSettings.deliveryDate!,
      postingDate: form.otherSettings.invoiceDate,
      dueDate: form.otherSettings.dueDate,
      showReferencePersonInAddress: !!form.otherSettings.showReferenceInAddress,
      showShippingAddress: !!form.otherSettings.showCustomerDeliveryAddress,
      showCurrencyCode: !!form.otherSettings.showCurrencyCode,
      language: form.otherSettings.languageKey ?? "",
      currency: form.currency ?? "",
      paymentOptionId: form.otherSettings.paymentMethodId,
      attachments: form.otherSettings.attachments as WintSuperkollAPIModel.InvoiceAttachment[],
      template: form.otherSettings.invoiceLayout as WintSuperkollAPIModel.InvoicePdfTemplate,
      useStrictValidation: true,
      creditedInvoiceId: invoice?.creditedInvoiceId,
      reminderCost: form.otherSettings.reminderCost,
      penaltyInterest: {
        type: form.otherSettings.penaltyInterestType!,
        rate:
          form.otherSettings.penaltyInterestPercentage !== undefined
            ? form.otherSettings.penaltyInterestPercentage / 100
            : undefined,
      },
      isAutomaticRemindersEnabled: form.otherSettings.isAutomaticRemindersEnabled,
      automaticReminderDelay: form.otherSettings.automaticReminderDelay,
      automaticReminderAmountLimit: form.otherSettings.automaticReminderAmountLimit,
      automaticReminderPeriod: form.otherSettings.automaticReminderPeriod,
      automaticReminderCountLimit: form.otherSettings.automaticReminderCountLimit,
      automaticReminderNotes: form.otherSettings.automaticReminderNotes,
      automaticReminderSendMethod: form.otherSettings.automaticReminderSendType,
      automaticReminderMailFromType: form.otherSettings.automaticReminderSendMail?.emailFromType,
      automaticReminderMailToType: InvoiceHelper.getEmailToType(
        form.mailOptions?.defaultEmailTo,
        form.otherSettings.automaticReminderSendMail?.emailTo
      ),
      automaticReminderMailToAddresses: form.otherSettings.automaticReminderSendMail?.emailTo
        ?.map((to) => to?.value)
        .filter((email) => email)
        .join(","),
      automaticReminderMailSubject: form.otherSettings.automaticReminderSendMail?.emailSubject?.replace(
        serialNumberTemplateValueDisplayText,
        InvoiceConstants.SerialNumberTemplateValue
      ),
      automaticReminderMailMessage: form.otherSettings.automaticReminderSendMail?.emailMessage,
      quotationSerialNumber: invoice?.quotationSerialNumber,
      customerPersonNumber:
        form.rutRot?.rutRotPersonNumber && !form.rutRot?.rutRotPersonNumber.includes("-")
          ? UtilsHelper.applyMask(form.rutRot?.rutRotPersonNumber, CommonConstants.PersonNumberLongMask)
          : form.rutRot?.rutRotPersonNumber,
      invoiceHouseWorkType:
        form && form.rutRot?.houseWorkType
          ? Number(form.rutRot?.houseWorkType)
          : WintSuperkollAPIModel.InvoiceHouseWorkType.None,
      invoiceRotPropertyType:
        form && form.rutRot?.rotPropertyType
          ? Number(form.rutRot?.rotPropertyType)
          : WintSuperkollAPIModel.InvoiceRotPropertyType.None,
      invoiceRotPercentageType: WintSuperkollAPIModel.InvoiceRotPercentageType.Standard,
      propertyDesignation: form.rutRot?.propertyDesignation,
      tenantsOwnerAssociationOrgNumber:
        form.rutRot?.tenantsOrgNumber && !form.rutRot?.tenantsOrgNumber.includes("-")
          ? UtilsHelper.applyMask(form.rutRot?.tenantsOrgNumber, CommonConstants.OrganizationNumberMask)
          : form.rutRot?.tenantsOrgNumber,
      apartmentNumber: form.rutRot?.apartmentNumber,
      houseWorkOtherCosts: houseWorkOtherCostInclVAT,
      invoiceSource: WintSuperkollAPIModel.InvoiceSource.Unknown,
      selfInvoiceId: invoice?.selfInvoiceId,
      invoiceMailInfo:
        invoice?.invoiceSendInfo?.invoiceSendMethod === WintSuperkollAPIModel.InvoiceSendMethod.Email
          ? {
              invoiceSendMethod: WintSuperkollAPIModel.InvoiceSendMethod.Email,
              mailOptions: {
                from: {
                  type:
                    form.previewSendMail?.emailFromType ?? WintSuperkollAPIModel.SendInvoiceMailsFromEnum.PersonMail,
                  address: form.previewSendMail?.emailFrom,
                },
                to: {
                  type: InvoiceHelper.getEmailToType(form.mailOptions?.defaultEmailTo, form.previewSendMail?.emailTo),
                  address: form.previewSendMail?.emailTo
                    ?.map((to) => to?.value)
                    .filter((email) => email)
                    .join(","),
                },
                subject: form.previewSendMail?.emailSubject,
                message: form.previewSendMail?.emailMessage,
              },
            }
          : invoice?.invoiceSendInfo,
      ...costForwardingFields,
      costMarkupPercentage: costForwardingFields.costMarkupPercentage ?? undefined,
    };
  };

  const convertFormToPreviewRequest = (
    form: InvoiceFormTypes.Create,
    noVatCompany: boolean,
    hideDeliveryDate: boolean,
    invoice?: WintSuperkollAPIModel.Invoice
  ): WintSuperkollAPIModel.InvoicePreviewRequest => {
    return convertInvoicePutToPreviewRequest(
      convertFormToAPIRequest(form, noVatCompany, hideDeliveryDate, invoice),
      invoice?.fromRecurring,
      invoice?.recurringInvoiceVersion,
      invoice?.recurringId
    );
  };

  const convertInvoicePutToPreviewRequest = (
    invoice: WintSuperkollAPIModel.InvoicePut,
    fromRecurring?: boolean,
    recurringInvoiceVersion?: WintSuperkollAPIModel.RecurringInvoiceVersion,
    recurringId?: number
  ): WintSuperkollAPIModel.InvoicePreviewRequest => {
    // The PDF preview never reads the attachments, and a forwarded cost's files would otherwise ride along in
    // every debounced preview request.
    const { attachments, ...withoutAttachments } = ForwardedCostHelper.withoutCostForwardingFields(invoice);
    const previewRequest: WintSuperkollAPIModel.InvoicePreviewRequest = {
      ...withoutAttachments,
      attachForwardedCostDocuments: false,
    };

    previewRequest.recurringInvoiceVersion = fromRecurring
      ? (recurringInvoiceVersion ?? WintSuperkollAPIModel.RecurringInvoiceVersion.V1_PeriodInfo)
      : WintSuperkollAPIModel.RecurringInvoiceVersion.V1_PeriodInfo;
    previewRequest.recurringInvoiceId = recurringId;
    return previewRequest;
  };

  const convertTodoFormToAPIRequest = (
    values: InvoiceFormTypes.Todo,
    invoice: WintSuperkollAPIModel.Invoice
  ): WintSuperkollAPIModel.InvoicePut => {
    const postingDate = values.useToday ? new Date() : values.otherSettings.invoiceDate;
    return {
      ...invoice,
      attachForwardedCostDocuments: false,
      // The reference person can be added or switched from the "Skicka som" box, which writes it into
      // the form values only — without this it never reaches the payload and the invoice goes out with
      // the reference the server invoice happened to carry.
      customerReference: values.referencePerson?.id
        ? {
            referenceId: values.referencePerson.id as number,
            referenceName: values.referencePerson.value,
          }
        : invoice.customerReference,
      postingDate: postingDate,
      dueDate: values.useToday ? DateHelper.addDays(postingDate, invoice.paymentTerms) : values.otherSettings.dueDate,
      notes: values.notes,
      attachments: values.attachments as WintSuperkollAPIModel.InvoiceAttachment[],
      rows: invoice.rows?.map((row) => {
        const product = { ...row };
        product.overriddenUnitPrice = product.unitPrice;
        product.overriddenVat = product.vat;

        if (invoice.dimensions && invoice.dimensions.length > 0) {
          product.dimensions = product.dimensions?.filter((v) => !invoice.dimensions?.includes(v));
        }

        if (!product.periodisation && !invoice.fromRecurring) return product;

        const periodisationStart = product.periodisationStartMonth
          ? DateHelper.splitYearMonth(product.periodisationStartMonth)
          : invoice?.fromRecurring
            ? DateHelper.getYearMonthByDate(postingDate)
            : undefined;

        const periodisationEnd = product.periodisationEndMonth
          ? DateHelper.splitYearMonth(product.periodisationEndMonth)
          : invoice?.fromRecurring
            ? DateHelper.getYearMonthByDate(postingDate)
            : undefined;

        if (periodisationStart) {
          product.periodisationStartMonth = periodisationStart.month;
          product.periodisationStartYear = periodisationStart.year;
        }

        if (periodisationEnd) {
          product.periodisationEndMonth = periodisationEnd.month;
          product.periodisationEndYear = periodisationEnd.year;
        }

        return product;
      }),
      useStrictValidation: true,
    };
  };

  const getRutRotWorkTypes = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetRutRotWorkTypes({
        ...ServiceConstants.DefaultFilter,
      } as WintSuperkollAPIModel.RutRotWorkTypeFilter)
      .then((res) => res.data.items ?? []);
  };

  const getHouseWorkTypes = (): SelectionItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceHouseWorkType)
      .filter((key) => key !== 0)
      .map((key) => {
        return {
          id: Number(key),
          value: LocalizationHelperInstance.getFormattedMessage(`Invoice.HouseWorkType_${key}`),
        };
      });
  };

  const getRotPropertyTypes = (): SelectionItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceRotPropertyType)
      .filter((key) => key !== 0)
      .map((key) => {
        return {
          id: Number(key),
          value: LocalizationHelperInstance.getFormattedMessage(`Invoice.RotPropertyType_${key}`),
        };
      });
  };

  const getRutRotTabHeader = (hasROT?: boolean, hasRUT?: boolean) => {
    if (hasROT && hasRUT) {
      return TranslationKeys.Invoice.RutRot;
    } else if (hasRUT) {
      return TranslationKeys.Invoice.HouseWorkType_2;
    } else if (hasROT) {
      return TranslationKeys.Invoice.HouseWorkType_1;
    } else {
      return "";
    }
  };

  const getDisabledHouseWorkTypes = (hasROT?: boolean, hasRUT?: boolean) => {
    const ids = [];
    if (!hasROT) {
      ids.push(WintSuperkollAPIModel.InvoiceHouseWorkType.ROT);
    }
    if (!hasRUT) {
      ids.push(WintSuperkollAPIModel.InvoiceHouseWorkType.RUT);
    }

    return ids;
  };

  const getRutOrRotText = (houseWorkType: WintSuperkollAPIModel.InvoiceHouseWorkType) => {
    switch (houseWorkType) {
      case WintSuperkollAPIModel.InvoiceHouseWorkType.ROT:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HouseWorkType_1);
      case WintSuperkollAPIModel.InvoiceHouseWorkType.RUT:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HouseWorkType_2);
      default:
        return "";
    }
  };

  const getEmailToUniqueType = (emailToType: any, email: string) => {
    return `${emailToType}_${email}`;
  };

  const disableAutomaticReminders = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).InvoiceClient.DisableAutomaticReminders(invoiceId);
  };

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useInvoiceSendTypeText)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const text = invoiceService.getSendAsTypeText(key);
   *
   * @example
   * // New way (recommended):
   * function useInvoiceSendTypeText() {
   *   const pageroEInvoice = useSelector((state: AppState) => state.auth.selectedCompany.pageroEInvoice);
   *
   *   const getSendAsTypeText = (key: number) => {
   *     return key === WintSuperkollAPIModel.InvoiceSendMethod.Print && !pageroEInvoice
   *       ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendType_Print)
   *       : LocalizationHelperInstance.getFormattedMessage(`Invoice.SendType_${key}`);
   *   };
   *
   *   return { getSendAsTypeText };
   * }
   *
   * // In component:
   * const { getSendAsTypeText } = useInvoiceSendTypeText();
   * const text = getSendAsTypeText(key);
   */
  const getSendAsTypeText = (key: number) => {
    return key === WintSuperkollAPIModel.InvoiceSendMethod.Print &&
      !store.getState().auth.selectedCompany.pageroEInvoice
      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendType_Print)
      : LocalizationHelperInstance.getFormattedMessage(`Invoice.SendType_${key}`);
  };

  const getVersionInfo = (invoiceId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetInvoiceVersions(invoiceId)
      .then((res) => res.data);
  };

  const getInvoicePersons = (searchStr: string, includeSelf?: boolean, excludeIds?: number[]) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetInvoicePersons({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        includeSelf: includeSelf ?? false,
        excludeIds: excludeIds ?? [],
      } as WintSuperkollAPIModel.InvoicePersonsFilter)
      .then((res) => (res.data.items ? res.data.items : []));
  };

  const getSuggestedInvoicePersons = (includeSelf?: boolean, excludeIds?: number[]) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetInvoicePersonSuggestions({
        count: ServiceConstants.SuggestionsCount,
        includeSelf: includeSelf ?? false,
        excludeIds: excludeIds ?? [],
      } as WintSuperkollAPIModel.InvoicePersonsSuggestionRequest)
      .then((res) => (res.data.items ? res.data.items : []));
  };

  const assignNewOwner = (invoiceId: number, request: WintSuperkollAPIModel.InvoiceOwnerReassignRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).InvoiceClient.ReassignOwner(invoiceId, request);
  };

  const calculateRutRotOtherCost = (
    products: InvoiceFormTypes.Product[],
    rutRotArticles: InvoiceFormTypes.RutRotArticleItem[]
  ) => {
    if (rutRotArticles.length === 0) {
      return 0;
    }
    const total = calculateTotalFromProducts(products);
    const totalLaborCost = calculateTotalFromProducts(
      products.filter((p) => rutRotArticles.some((r) => r.id === p.uniqueId))
    );
    const totalMaterialCost = rutRotArticles.map((r) => r.materialCost ?? 0).reduce((a, b) => +a + +b, 0);
    const otherCost = total - totalLaborCost - totalMaterialCost;
    return otherCost >= 0 ? otherCost : 0;
  };

  function formatInvoiceFormMasterRowConfigurations<T extends InvoiceFormTypes.CreateBase>(formValues: T): T {
    return {
      ...formValues,
      products: formValues.products?.map((product) => {
        const periodisation = formValues.isPeriodisationAdvancedConfiguration
          ? product.periodisation
          : formValues.products![0].periodisation;

        const dimensions = formValues.isDimensionsAdvancedConfiguration
          ? product.dimensions
          : formValues.products![0].dimensions;

        return {
          ...product,
          periodisation: periodisation,
          dimensions: dimensions,
        };
      }),
    };
  }

  const getInvoiceSources = (): SelectionItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSource).map((key) => {
      return {
        id: Number(key),
        value: LocalizationHelperInstance.getFormattedMessage(`Invoice.Source_${key}`),
      };
    });
  };

  const activateInvoices = (values: WintSuperkollAPIModel.InvoiceActivationRequest) => {
    return ApiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
      hideErrorsForCodes: [
        InvoiceConstants.InvoicesActicationInProgressErrorCode,
        InvoiceConstants.InvoicesAlreadyActivatedErrorCode,
      ],
    }).InvoiceClient.Activate(values);
  };

  const getInvoiceSendMethodText = (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => {
    return LocalizationHelperInstance.getFormattedMessage(
      sendType === WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent
        ? TranslationKeys.Common.Download
        : TranslationKeys.Common.Send
    );
  };

  const getInvioceNextSerialNumber = () => {
    return ApiClientInstance(AuthEnums.AuthType.Company)
      .InvoiceClient.GetNextCompanyInvoiceSerialNumber()
      .then((res) => res.data);
  };

  return {
    get,
    deleteInvoice,
    reminderPayment,
    getByFilter,
    postClaim,
    getCustomerLink,
    editDueDate,
    reportAsPaid,
    createInvoice,
    updateInvoice,
    createOrUpdateInvoice,
    sendAsEmail,
    emailInvoice,
    sendAsPrint,
    printInvoice,
    markAsSent,
    downloadPdf,
    getInvoicePdf,
    createReferencePerson,
    updateReferencePerson,
    postAttachments,
    getAttachment,
    getInvoiceMailForm,
    getUIViewState,
    getStateText,
    getAccountingStateText,
    getTransactions,
    getSimilarInvoices,
    getStateLogs,
    customerReferencePersons,
    getReferencePersonSuggestions,
    searchReferencePersons,
    searchReferencePersonsByMail,
    getUnits,
    generateInvoicePreview,
    getInvoicePDFPreview,
    getInvoiceReminderPDFPreview,
    getSellers,
    getPaymentOptions,
    getArticleSuggestions,
    searchArticles,
    getCompatibleDimensionTypes,
    updateDimensions,
    getEuVatCategoryTypes,
    getEuVatCategoryText,
    calculateTax,
    calculateAmount,
    calculateTotalFromProducts,
    getRowDimensionDataAdvancedConfig,
    getRowDimensionData,
    getInvoiceDimensionData,
    getInvoiceDimensions,
    getUpdatedInvoice,
    getCreditInvoice,
    getRecurringFromInvoice,
    getCopiedInvoice,
    getInvoiceWarnings,
    reportFilter,
    reportToAPIFilter,
    getPaymentStatuses,
    getPaymentStateText,
    getCreditStatuses,
    getPaymentViewStatuses,
    getInvoiceStatuses,
    toViewState,
    toApiState,
    toViewPaymentState,
    toApiViewPaymentStates,
    getTodoState,
    getTodoStateLabelClass,
    getProducts,
    convertFormToAPIRequest,
    convertFormToPreviewRequest,
    convertInvoicePutToPreviewRequest,
    convertTodoFormToAPIRequest,
    getRutRotWorkTypes,
    getHouseWorkTypes,
    getRotPropertyTypes,
    getRutRotTabHeader,
    getDisabledHouseWorkTypes,
    getRutOrRotText,
    getEmailToUniqueType,
    disableAutomaticReminders,
    getSendAsTypeText,
    getVersionInfo,
    getInvoicePersons,
    getSuggestedInvoicePersons,
    assignNewOwner,
    calculateRutRotOtherCost,
    formatInvoiceFormMasterRowConfigurations,
    getInvoiceSources,
    activateInvoices,
    getInvoiceSendMethodText,
    getInvioceNextSerialNumber,
    editPeriodisations,
    writeOffReminderFee,
  };
}

export default useInvoiceService;
